import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { getApplicationOptions } from '../api';
import { fatalError } from '../actions/application';
import {
  selectAkHash,
  selectLocationStreet,
  selectLocationCity,
  selectLocationState,
  selectLocationZip,
} from '../selectors/application';
import { selectQuoteId } from '../selectors/quote';
import { useFeatureFlags } from '../toggle_tools/featureFlagTools';
import { QUOTE_COVERAGE_DEFAULTS } from '../constants';

interface SelectedLocationLimit {
  min: number;
  max: number;
  default: number;
  hasCoastalRestriction: boolean;
}

// The pre-endpoint building limit rule, expressed as bounds rather than as a branch in each
// editor so every consumer runs one code path. Dies with feUseDynamicBopLimits.
const LEGACY_BUILDING_LIMIT_MIN = 25_000;
const LEGACY_BUILDING_LIMIT_MAX = 1_000_000;

const hasCoastalFactor = (restrictingFactors: string[] | undefined) =>
  restrictingFactors?.includes('DistanceToCoast') ?? false;

export interface SelectedApplicationOptions {
  bppDeductible: {
    allowedValues: number[];
    default: number;
    hasCoastalRestriction: boolean;
  };
  windHailDeductible: {
    allowedValues: WindHailDeductible[];
    default: WindHailDeductible;
    hasCoastalRestriction: boolean;
  };
  bppLimit: SelectedLocationLimit;
  buildingLimit: SelectedLocationLimit;
  professionalLiability: PLApplicationOptions;
}

const useGetApplicationOptions = () => {
  const dispatch = useDispatch();
  const akHash = useSelector(selectAkHash);
  const street = useSelector(selectLocationStreet);
  const city = useSelector(selectLocationCity);
  const state = useSelector(selectLocationState);
  const zip = useSelector(selectLocationZip);
  const quoteId = useSelector(selectQuoteId);
  const { feUseDynamicBopLimits } = useFeatureFlags();
  const quoteExternalId = feUseDynamicBopLimits ? quoteId : undefined;

  const enabled = Boolean(akHash) && Boolean(street) && Boolean(city) && Boolean(state) && Boolean(zip);

  const query = useQuery(
    ['application-options', { akHash, street, city, state, zip, quoteExternalId }],
    () =>
      getApplicationOptions({
        akHash,
        street,
        city,
        state,
        zip,
        quoteExternalId,
      }),
    {
      enabled,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      select: (data): SelectedApplicationOptions => ({
        bppDeductible: {
          allowedValues: data?.bppDeductible?.allowedValues,
          default: data?.bppDeductible?.default,
          hasCoastalRestriction: hasCoastalFactor(data?.bppDeductible?.restrictingFactors),
        },
        windHailDeductible: {
          allowedValues: data?.windHailDeductible?.allowedValues,
          default: data?.windHailDeductible?.default,
          hasCoastalRestriction: hasCoastalFactor(data?.windHailDeductible?.restrictingFactors),
        },
        bppLimit: {
          // min/max have been API-driven since QV2-2338 and are not in this flag's scope.
          // Only the default moves with it.
          min: data?.locations?.bppLimit?.min,
          max: data?.locations?.bppLimit?.max,
          default: feUseDynamicBopLimits ? data?.locations?.bppLimit?.default : QUOTE_COVERAGE_DEFAULTS.BPP_LIMIT,
          hasCoastalRestriction: hasCoastalFactor(data?.locations?.bppLimit?.restrictingFactors),
        },
        buildingLimit: feUseDynamicBopLimits
          ? {
              min: data?.locations?.buildingLimit?.min,
              max: data?.locations?.buildingLimit?.max,
              default: data?.locations?.buildingLimit?.default,
              hasCoastalRestriction: hasCoastalFactor(data?.locations?.buildingLimit?.restrictingFactors),
            }
          : {
              min: LEGACY_BUILDING_LIMIT_MIN,
              max: LEGACY_BUILDING_LIMIT_MAX,
              default: data?.businessOwnersPolicy?.defaultBuildingLimit,
              hasCoastalRestriction: false,
            },
        professionalLiability: data?.professionalLiability,
      }),
    }
  );

  useEffect(() => {
    if (query.isError) {
      dispatch(fatalError(query.error));
    }
  }, [query.isError, query.error, dispatch]);

  return Object.assign(query, { isAwaitingOptions: enabled && query.data === undefined });
};

export default useGetApplicationOptions;
